import { useEffect, useState, useRef, useCallback } from "react";
import { Send, MessageSquare, ArrowLeft, ArrowRight, Headphones, User, Circle } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { formatLastSeen, isOnline } from "@/lib/presence";

/**
 * Reusable ChatView used inside /chat page and support drawer.
 * Props: convId (string), onBack (fn optional).
 */
export default function ChatView({ convId, onBack, embedded = false }) {
    const { t, lang, isRtl } = useLang();
    const { user } = useAuth();
    const [data, setData] = useState({ messages: [], conversation: null });
    const [text, setText] = useState("");
    const [sending, setSending] = useState(false);
    const scrollRef = useRef(null);
    const lastIsoRef = useRef(null);
    const pollRef = useRef(null);

    const scrollToBottom = () => {
        setTimeout(() => {
            if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }, 30);
    };

    const load = useCallback(async (initial = false) => {
        if (!convId) return;
        try {
            const params = initial || !lastIsoRef.current ? {} : { since: lastIsoRef.current };
            const { data: res } = await api.get(`/chat/conversations/${convId}/messages`, { params });
            if (initial || !lastIsoRef.current) {
                setData({ messages: res.messages, conversation: res.conversation });
                if (res.messages.length) lastIsoRef.current = res.messages[res.messages.length - 1].created_at;
                scrollToBottom();
            } else if (res.messages.length > 0) {
                setData((d) => ({ ...d, messages: [...d.messages, ...res.messages], conversation: res.conversation }));
                lastIsoRef.current = res.messages[res.messages.length - 1].created_at;
                scrollToBottom();
            } else {
                setData((d) => ({ ...d, conversation: res.conversation }));
            }
        } catch (e) {
            /* ignore */
        }
    }, [convId]);

    useEffect(() => {
        lastIsoRef.current = null;
        setData({ messages: [], conversation: null });
        if (convId) load(true);
        if (pollRef.current) clearInterval(pollRef.current);
        if (convId) {
            pollRef.current = setInterval(() => load(false), 4000);
        }
        return () => {
            if (pollRef.current) clearInterval(pollRef.current);
        };
    }, [convId, load]);

    const send = async (e) => {
        e?.preventDefault?.();
        const value = text.trim();
        if (!value) return;
        setSending(true);
        try {
            await api.post(`/chat/conversations/${convId}/messages`, { text: value });
            setText("");
            await load(false);
            scrollToBottom();
        } catch {
            toast.error("Error");
        } finally { setSending(false); }
    };

    if (!convId) {
        return (
            <div className="grid place-items-center h-full text-muted-foreground text-sm p-10">
                <div className="text-center">
                    <MessageSquare className="w-10 h-10 mx-auto mb-3 opacity-40" />
                    {t("selectConversation")}
                </div>
            </div>
        );
    }

    const conv = data.conversation;
    const isSupport = conv?.kind === "support";
    const peerName = conv?.peer_name || (isSupport ? t("supportChat") : "");
    const peerOnline = isOnline(conv?.peer_last_seen);
    const lastSeenText = isSupport
        ? (peerOnline ? t("online") : t("supportChat"))
        : formatLastSeen(conv?.peer_last_seen, t);

    return (
        <div className={`flex flex-col h-full ${embedded ? "" : "bg-white"}`} data-testid="chat-view">
            {/* Header */}
            <div className="flex items-center gap-3 p-4 border-b border-border bg-white">
                {onBack && (
                    <button onClick={onBack} className="p-2 -m-2 rounded-full hover:bg-surface" aria-label="Back" data-testid="chat-back-btn">
                        {isRtl ? <ArrowRight className="w-5 h-5" /> : <ArrowLeft className="w-5 h-5" />}
                    </button>
                )}
                <div className="w-10 h-10 rounded-full bg-surface grid place-items-center overflow-hidden flex-shrink-0">
                    {isSupport ? <Headphones className="w-5 h-5 text-terracotta" /> : conv?.peer_picture ? <img src={conv.peer_picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5 text-muted-foreground" />}
                </div>
                <div className="flex-1 min-w-0">
                    <div className="font-bold text-espresso truncate" data-testid="chat-peer-name">{peerName || "..."}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="chat-last-seen">
                        {peerOnline && <Circle className="w-2 h-2 fill-green-500 text-green-500" />}
                        <span>{lastSeenText}</span>
                    </div>
                </div>
            </div>

            {/* Messages */}
            <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-surface/40" data-testid="chat-messages">
                {data.messages.length === 0 && (
                    <div className="text-center text-muted-foreground text-xs mt-8">
                        {isSupport ? t("supportWelcome") : t("startConversation")}
                    </div>
                )}
                {data.messages.map((m) => {
                    const mine = m.sender_id === user.user_id || (user.role === "admin" && m.sender_id === "SUPPORT");
                    return (
                        <div key={m.message_id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.message_id}`}>
                            <div className={`max-w-[80%] px-4 py-2 rounded-2xl text-sm ${mine ? "bg-terracotta text-white rounded-br-sm" : "bg-white border border-border text-espresso rounded-bl-sm"}`}>
                                <div className="whitespace-pre-wrap break-words">{m.text}</div>
                                <div className={`text-[10px] mt-1 ${mine ? "text-white/70" : "text-muted-foreground"}`}>
                                    {new Date(m.created_at).toLocaleTimeString(lang === "ar" ? "ar-SA" : "en", { hour: "2-digit", minute: "2-digit" })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Composer */}
            <form onSubmit={send} className="p-3 border-t border-border flex gap-2 bg-white" data-testid="chat-composer">
                <Input
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={t("typeMessage")}
                    className="flex-1 h-11 rounded-full px-5"
                    data-testid="chat-input"
                />
                <Button type="submit" disabled={sending || !text.trim()} className="rounded-full h-11 w-11 p-0 bg-terracotta hover:bg-terracotta/90" data-testid="chat-send-btn" aria-label={t("send")}>
                    <Send className={`w-4 h-4 ${isRtl ? "rotate-180" : ""}`} />
                </Button>
            </form>
        </div>
    );
}
